<script setup lang="ts">
  import { computed, onMounted, ref, watch } from 'vue'
  import {
    ActaportPartyMatchingService,
    CaseCreationIntegrationService,
    CasesService,
    FormsService,
    OrganisationsService
  } from '@/types/generated/services.gen'
  import {
    AiAugmentedByMapperKeysEnum,
    type ActaportDataRequest,
    type ActaportPartyMatchingResponse,
    type CustomerCase,
    EntitiesMapperKeyEnum,
    EntityTypeEnum,
    type ExternalIntegrationFileDownloadResponse,
    type FileDataRequest,
    FlagEnum,
    type IntegrationSpecificDataRequest,
    type SchemaExternalIntegrationCaseCreationIntegrationTypeEnum as IntegrationTypeEnum,
    type LinkDecisionValueRequest,
    type PatchedCustomerCaseRequest,
    type ScheduledExportRequestIntegrationTypeEnum,
    type GenericDataMapper,
    type LawData,
    type SchemaExternalIntegrationCaseCreationRequest
  } from '@/types/generated/types.gen'
  import router from '@/router'
  import ActaportForm from '@/components/integrations/actaport.vue'
  import { ActaportPartyMatchingStep } from '@/components/integrations/actaport'
  import KleosForm from '@/components/integrations/kleos.vue'
  import PersonalInformation from '@/components/integrations/personalInformation.vue'
  import OpponentInformation from '@/components/integrations/opponentInformation.vue'
  import LegalInsurance from '@/components/integrations/legalInsuranceSection.vue'
  import SelectDocumentSection from '@/components/integrations/selectDocumentSection.vue'
  import { ArrowLeftIcon } from '@heroicons/vue/24/solid'
  import AdvowareForm from '@/components/integrations/advoware.vue'
  import AdvowareRolePreview from '@/components/integrations/AdvowareRolePreview.vue'
  import JurnodesForm from '@/components/integrations/jurnodes.vue'
  import AdvoluxForm from '@/components/integrations/advolux.vue'
  import LawFirmSoftwareForm from '@/components/integrations/lawfirm.vue'
  import RaMicroForm from '@/components/integrations/ramicro.vue'
  import RaMicroApiForm from '@/components/integrations/ramicroapi.vue'
  import DatevForm from '@/components/integrations/datev.vue'
  import LoadingComponent from '@/components/integrations/loadingPage.vue'
  import Entities from '@/components/app/cases/entities/Entities.vue'
  import DatamapperTable from '@/components/app/cases/DatamapperTable.vue'
  import PendingProposedChangesDialog from '@/components/app/cases/PendingProposedChangesDialog.vue'
  import { useToast } from '@/components/ui/toast'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import { yup } from '@/lib/validation.ts'
  import { debounce } from '@/lib/timing'
  import { useTranslation } from '@/composables/useTranslation'
  import { useFeatureFlag } from '@/composables/useFeatureFlag'

  export type EntityFieldsGroup =
    | 'companyInfo'
    | 'institutionInfo'
    | 'personalInfo'
    | 'addressInfo'
    | 'bankingInfo'
    | 'legalInsurance'

  export type EntityTypeValidation = {
    [entityType in EntityTypeEnum]?: Record<string, yup.AnySchema>
  }

  export type CustomExportValidation = {
    [intType in IntegrationTypeEnum | 'base']?: {
      [group in EntityFieldsGroup]?: Record<string, yup.AnySchema>
    } & {
      entityTypeValidation?: EntityTypeValidation
    }
  }

  const { t } = useTranslation({
    en: {
      backToCase: 'Back to Case',
      exportLegalInsurance: 'Export legal insurance data',
      exportOpponent: 'Export opponent information',
      errorTitle: 'Error',
      loadingDataText: 'Loading data...',
      failedToLoadCaseData: 'Failed to load case data. Please try again.',
      failedToLoadData: 'Failed to load data. Please try again.',
      allRequiredFieldsNotFilled: 'Not all required fields for export have been filled.',
      fillRequiredFieldsForSelectedEntities:
        'Please fill in the required fields for all selected entities.',
      documentSizeExceedsLimit: 'The total size of selected documents exceeds the 10MB limit.',
      exportToRaMicro: 'Export Request to RA-MICRO',
      exportToIntegration: 'Export Request to {integration}',
      downloadCase: 'Download Case',
      createCase: 'Create Case',
      downloadingCase: 'Case is being downloaded...',
      creatingCase: 'Case is being created...',
      warning: 'Warning',
      actaportSubrolesWarning:
        'Currently, this export function does not support automatic assignment of sub-roles to case participants when exporting to Actaport. Please check the sub-roles manually in Actaport after export.',
      naturalPerson: 'Natural Person',
      legalPerson: 'Legal Person',
      institution: 'Institution',
      validDateFormatRequired: 'Please enter a valid date in DD.MM.YYYY format',
      existingDateFormatRequired: 'Please enter an existing date in DD.MM.YYYY format for {name}',
      dateOfBirthMustBeInPast: 'The date of birth of {name} must be in the past.',
      dateOfBirthMustBeInPastCompany:
        'The date of birth of the contact info of {companyName} must be in the past.',
      dateOfBirthMustBeInPastInstitution:
        'The date of birth of the contact info of {institutionName} must be in the past.',
      lastNameRequired: 'Last name is required.',
      lastNameRequiredFor: 'Last name for {firstName} is required.',
      companyNameRequired: 'Company name is required.',
      institutionNameRequired: 'Institution name is required.',
      phoneNumberMinLength: 'Phone number must be empty or contain at least 3 characters',
      streetMaxLength: 'Street must be at most {max} characters',
      streetNumberMaxLength: 'Street number must be at most {max} characters',
      zipCodeMaxLength: 'ZIP code must be at most {max} characters',
      cityMaxLength: 'City must be at most {max} characters',
      bankNameMaxLength: 'Bank name must be at most {max} characters',
      firstNameRequired: 'First name is required',
      advowareAuthError:
        'Advoware authentication failed. This may be due to incorrect credentials, the Advoware relay not being connected, or network connectivity issues. Please verify your settings under Settings → Law Office Software → Advoware and ensure your Advoware system is accessible.\n\nDetails: {details}',
      advowareApiError:
        'Advoware API error: There was a problem communicating with the Advoware interface. Please check your connection and settings.\n\nDetails: {details}',
      temporaryErrorMessage:
        'The export could not be started because the {integration} service is currently unavailable. This is a technical issue on the {integration} side.\n\nPlease choose how you would like to proceed:',
      scheduleExportError: 'An error occurred while scheduling the export.',
      lawRelevantDataJurnodes: 'Law-specific data',
      updatedFields: 'Updated fields:'
    },
    de: {
      backToCase: 'Zurück zur Anfrage',
      exportLegalInsurance: 'Rechtsschutzversicherung exportieren',
      exportOpponent: 'Gegnerinformationen exportieren',
      errorTitle: 'Fehler',
      loadingDataText: 'Daten werden geladen...',
      failedToLoadCaseData:
        'Falldaten konnten nicht geladen werden. Bitte versuchen Sie es erneut.',
      failedToLoadData: 'Daten konnten nicht geladen werden. Bitte versuchen Sie es erneut.',
      allRequiredFieldsNotFilled:
        'Es wurden nicht alle erforderlichen Felder für den Export ausgefüllt.',
      fillRequiredFieldsForSelectedEntities:
        'Bitte füllen Sie die erforderlichen Felder für alle ausgewählten Beteiligten aus.',
      documentSizeExceedsLimit:
        'Die Gesamtgröße der ausgewählten Dokumente überschreitet das Limit von 10MB.',
      exportToRaMicro: 'Anfrage an RA-MICRO exportieren',
      exportToIntegration: 'Anfrage an {integration} exportieren',
      downloadCase: 'Akte herunterladen',
      createCase: 'Akte anlegen',
      downloadingCase: 'Akte wird heruntergeladen...',
      creatingCase: 'Akte wird angelegt...',
      warning: 'Achtung',
      actaportSubrolesWarning:
        'Derzeit unterstützt diese Exportfunktion nicht die automatische Zuweisung von Unterrollen zu Verfahrensbeteiligten beim Export nach Actaport. Bitte überprüfen Sie die Unterrollen nach dem Export manuell in Actaport.',
      naturalPerson: 'Natürliche Person',
      legalPerson: 'Juristische Person',
      institution: 'Institution',
      validDateFormatRequired: 'Bitte geben Sie ein gültiges Datum im Format TT.MM.JJJJ ein',
      existingDateFormatRequired:
        'Bitte geben Sie ein existierendes Datum im Format TT.MM.JJJJ für {name} ein',
      dateOfBirthMustBeInPast: 'Das Geburtsdatum von {name} muss in der Vergangenheit liegen.',
      dateOfBirthMustBeInPastCompany:
        'Das Geburtsdatum von den Contact Info von {companyName} muss in der Vergangenheit liegen.',
      dateOfBirthMustBeInPastInstitution:
        'Das Geburtsdatum von den Contact Info von {institutionName} muss in der Vergangenheit liegen.',
      lastNameRequired: 'Der Nachname ist erforderlich.',
      lastNameRequiredFor: 'Der Nachname von {firstName} ist erforderlich.',
      companyNameRequired: 'Der Firmenname ist erforderlich.',
      institutionNameRequired: 'Der Name der Institution ist erforderlich.',
      phoneNumberMinLength: 'Die Telefonnummer muss leer sein oder mindestens 3 Zeichen enthalten',
      streetMaxLength: 'Straße darf höchstens {max} Zeichen lang sein',
      streetNumberMaxLength: 'Hausnummer darf höchstens {max} Zeichen lang sein',
      zipCodeMaxLength: 'PLZ darf höchstens {max} Zeichen lang sein',
      cityMaxLength: 'Stadt darf höchstens {max} Zeichen lang sein',
      bankNameMaxLength: 'Bankname darf höchstens {max} Zeichen lang sein',
      firstNameRequired: 'Vorname ist erforderlich',
      advowareAuthError:
        'Advoware-Authentifizierung fehlgeschlagen. Dies kann an ungültigen Zugangsdaten, einer nicht verbundenen Advoware-Schnittstelle oder Netzwerkproblemen liegen. Bitte überprüfen Sie Ihre Einstellungen unter Einstellungen → Kanzleisoftware → Advoware und stellen Sie sicher, dass Ihr Advoware-System erreichbar ist.\n\nDetails: {details}',
      advowareApiError:
        'Advoware API-Fehler: Es gab ein Problem bei der Kommunikation mit der Advoware-Schnittstelle. Bitte überprüfen Sie Ihre Verbindung und Einstellungen.\n\nDetails: {details}',
      temporaryErrorMessage:
        'Der Export konnte gerade nicht gestartet werden, da der {integration} Dienst momentan nicht erreichbar ist. Das ist ein technischer Fehler auf Seiten von {integration}.\n\nBitte wählen Sie, wie Sie fortfahren möchten:',
      scheduleExportError: 'Beim Planen des Exports ist ein Fehler aufgetreten.',
      lawRelevantDataJurnodes: 'Rechtsbezogene Daten',
      updatedFields: 'Aktualisierte Felder:'
    }
  })

  const caseStore = useCaseStore()
  const { toast } = useToast()

  const exportRetriesFlag = useFeatureFlag(FlagEnum.EXPORT_RETRIES)

  const caseData = ref<CustomerCase | null>(null)
  const isLoading = ref(true)
  const isEntitiesLoading = ref(true)
  const isFormsLoading = ref(true)
  const error = ref<string | null>(null)
  const showErrorNotification = ref(false)
  const documentSizeValid = ref(true)
  const isOpponentExported = ref(false)
  const exportLegalInsuranceData = ref(false)
  const totalNumberOfErrors = ref(0)
  const selectedEntitiesRelationships = ref<Array<string>>([])
  const selectedLawSpecificMapperKeys = ref<string[]>([])
  const prevNonEmptyLawSpecificKeys = ref<string[]>([])
  const jurnodesExportMapperKeys = ref<string[]>([])

  const showPartyMatchingStep = ref(false)
  const partyMatchingResult = ref<ActaportPartyMatchingResponse | null>(null)
  const partyLinkSelections = ref<Map<string, LinkDecisionValueRequest>>(new Map())
  const isLoadingPartyMatches = ref(false)
  const hasCheckedForMatches = ref(false)

  const personalInformationRef = ref<InstanceType<typeof PersonalInformation> | null>(null)
  const opponentInformationRef = ref<InstanceType<typeof OpponentInformation> | null>(null)
  const actaportFormRef = ref<InstanceType<typeof ActaportForm> | null>(null)
  const advowareFormRef = ref<InstanceType<typeof AdvowareForm> | null>(null)
  const kleosFormRef = ref<InstanceType<typeof KleosForm> | null>(null)
  const jurnodesFormRef = ref<InstanceType<typeof JurnodesForm> | null>(null)
  const advoluxFormRef = ref<InstanceType<typeof AdvoluxForm> | null>(null)
  const lawFirmSoftwareFormRef = ref<InstanceType<typeof LawFirmSoftwareForm> | null>(null)
  const raMicroFormRef = ref<InstanceType<typeof RaMicroForm> | null>(null)
  const raMicroApiFormRef = ref<InstanceType<typeof RaMicroApiForm> | null>(null)
  const legalInsuranceRef = ref<InstanceType<typeof LegalInsurance> | null>(null)
  const datevFormRef = ref<InstanceType<typeof DatevForm> | null>(null)
  const entitiesRef = ref<InstanceType<typeof Entities> | null>(null)

  const validationErrors = ref<Record<string, any>>({})
  const showModal = ref(false)
  const modalStatus = ref<'loading' | 'success' | 'error' | 'temporary_error' | 'scheduled'>(
    'loading'
  )
  const errorMessage = ref<string>(t('allRequiredFieldsNotFilled'))
  const exportErrorDetails = ref<string>('')
  const exportWarnings = ref<string[]>([])
  const canScheduleExport = ref(false)
  const isSchedulingExport = ref(false)
  const isRetryingExport = ref(false)
  // Track if user already had a manual retry fail to avoid showing the same modal again
  const hasManualRetryFailed = ref(false)
  const showPendingChangesDialog = ref(false)

  const apiPayload = ref<SchemaExternalIntegrationCaseCreationRequest>({
    integrationType: 'actaport', // Default value, will be updated
    mandateId: 0, // Will be updated when case data is loaded
    integrationSpecificData: {
      export_opponent_information: false,
      use_legal_insurance: false,
      entities_with_relationships: []
    } as IntegrationSpecificDataRequest
  })

  const opponentEnabledList = [
    'Actaport',
    'Advoware',
    'Advolux',
    'Jurnodes',
    'RaMicro',
    'RaMicroApi',
    'Kleos',
    'LawFirmSoftware',
    'DATEV'
  ]

  const miscEntitiesEnabledList = [
    'Actaport',
    'Advoware',
    'Advolux',
    'Jurnodes',
    'RaMicro',
    'RaMicroApi',
    'Kleos',
    'LawFirmSoftware',
    'DATEV'
  ]

  const baseValidation: CustomExportValidation = {
    base: {
      personalInfo: {
        [EntitiesMapperKeyEnum.ENTITIES_FIELD_DATE_OF_BIRTH]: yup
          .string()
          .nullable()
          .matches(
            /^$|^(0[1-9]|[12][0-9]|3[01])\.(0[1-9]|1[0-2])\.(19|20)\d{2}$/,
            t('validDateFormatRequired')
          ),
        [EntitiesMapperKeyEnum.ENTITIES_FIELD_PHONE]: yup
          .string()
          .nullable()
          .notRequired()
          .test(
            'empty-or-min-3',
            t('phoneNumberMinLength'),
            (value) => value === '' || value == null || value.length >= 3
          )
      },
      companyInfo: {
        [EntitiesMapperKeyEnum.ENTITIES_FIELD_COMPANY_NAME]: yup.string().nullable()
      },
      institutionInfo: {
        [EntitiesMapperKeyEnum.ENTITIES_FIELD_INSTITUTION_NAME]: yup.string().nullable()
      },
      addressInfo: {
        [EntitiesMapperKeyEnum.ENTITIES_FIELD_ZIP]: yup
          .string()
          .max(12, t('zipCodeMaxLength', { max: 12 }))
          .nullable()
      },
      legalInsurance: {
        [EntitiesMapperKeyEnum.ENTITIES_FIELD_LEGAL_INSURANCE]: yup
          .boolean()
          .nullable()
          .notRequired(),
        [EntitiesMapperKeyEnum.ENTITIES_FIELD_LEGAL_INSURANCE_DEDUCTIBLE_AMOUNT]: yup
          .number()
          .nullable()
          .notRequired()
      },
      entityTypeValidation: {
        [EntityTypeEnum.NATURAL_PERSON]: {
          [EntitiesMapperKeyEnum.ENTITIES_FIELD_LAST_NAME]: yup
            .string()
            .nullable()
            .test('required-for-natural-person', t('lastNameRequired'), function (value) {
              return value != null && value.trim() !== ''
            })
        },
        [EntityTypeEnum.LEGAL_PERSON]: {
          [EntitiesMapperKeyEnum.ENTITIES_FIELD_COMPANY_NAME]: yup
            .string()
            .nullable()
            .test('required-for-legal-person', t('companyNameRequired'), function (value) {
              return value != null && value.trim() !== ''
            })
        },
        [EntityTypeEnum.INSTITUTION]: {
          [EntitiesMapperKeyEnum.ENTITIES_FIELD_INSTITUTION_NAME]: yup
            .string()
            .nullable()
            .test('required-for-institution', t('institutionNameRequired'), function (value) {
              return value != null && value.trim() !== ''
            })
        }
      }
    }
  }

  // Advoware API field length limits
  // Note: street_number (model max 16) and zip (model max 12) are already below
  // Advoware's limits (39 and 20), so no overrides needed for those fields.
  const ADVOWARE_MAX_STREET_LENGTH = 50
  const ADVOWARE_MAX_CITY_LENGTH = 39

  // Actaport API field length limits (must match backend ContactValidator constants)
  const ACTAPORT_MAX_STREET_LENGTH = 50
  const ACTAPORT_MAX_CITY_LENGTH = 40
  const ACTAPORT_MAX_ZIP_LENGTH = 10
  const ACTAPORT_MAX_BANK_NAME_LENGTH = 50

  const integrationValidationOverrides: CustomExportValidation = {
    advoware: {
      addressInfo: {
        [EntitiesMapperKeyEnum.ENTITIES_FIELD_STREET]: yup
          .string()
          .max(
            ADVOWARE_MAX_STREET_LENGTH,
            t('streetMaxLength', { max: ADVOWARE_MAX_STREET_LENGTH })
          )
          .nullable(),
        [EntitiesMapperKeyEnum.ENTITIES_FIELD_CITY]: yup
          .string()
          .max(ADVOWARE_MAX_CITY_LENGTH, t('cityMaxLength', { max: ADVOWARE_MAX_CITY_LENGTH }))
          .nullable()
      }
    },
    actaport: {
      addressInfo: {
        [EntitiesMapperKeyEnum.ENTITIES_FIELD_STREET]: yup
          .string()
          .max(
            ACTAPORT_MAX_STREET_LENGTH,
            t('streetMaxLength', { max: ACTAPORT_MAX_STREET_LENGTH })
          )
          .nullable(),
        [EntitiesMapperKeyEnum.ENTITIES_FIELD_ZIP]: yup
          .string()
          .max(ACTAPORT_MAX_ZIP_LENGTH, t('zipCodeMaxLength', { max: ACTAPORT_MAX_ZIP_LENGTH }))
          .nullable(),
        [EntitiesMapperKeyEnum.ENTITIES_FIELD_CITY]: yup
          .string()
          .max(ACTAPORT_MAX_CITY_LENGTH, t('cityMaxLength', { max: ACTAPORT_MAX_CITY_LENGTH }))
          .nullable()
      },
      bankingInfo: {
        [EntitiesMapperKeyEnum.ENTITIES_FIELD_BANK_NAME]: yup
          .string()
          .max(
            ACTAPORT_MAX_BANK_NAME_LENGTH,
            t('bankNameMaxLength', { max: ACTAPORT_MAX_BANK_NAME_LENGTH })
          )
          .nullable()
      }
    },
    kleos: {
      entityTypeValidation: {
        [EntityTypeEnum.NATURAL_PERSON]: {
          [EntitiesMapperKeyEnum.ENTITIES_FIELD_FIRST_NAME]: yup
            .string()
            .required(t('firstNameRequired'))
        }
      }
    }
  }

  const createValidationForIntegration = (integrationType: IntegrationTypeEnum) => {
    const baseConfig = baseValidation.base || {}
    const overrideConfig = integrationValidationOverrides[integrationType] || {}

    const result: {
      [group in EntityFieldsGroup]?: Record<string, yup.AnySchema>
    } & {
      entityTypeValidation?: EntityTypeValidation
    } = {}

    // Merge only real field groups, exclude the meta key `entityTypeValidation`
    const allGroups = new Set([...Object.keys(baseConfig), ...Object.keys(overrideConfig)])
    const groupsToMerge = Array.from(allGroups).filter(
      (g) => g !== 'entityTypeValidation'
    ) as EntityFieldsGroup[]

    groupsToMerge.forEach((group) => {
      result[group] = {
        ...baseConfig[group],
        ...overrideConfig[group]
      }
    })

    if (baseConfig.entityTypeValidation || overrideConfig.entityTypeValidation) {
      const allEntityTypes = new Set([
        ...Object.keys(baseConfig.entityTypeValidation || {}),
        ...Object.keys(overrideConfig.entityTypeValidation || {})
      ])

      result.entityTypeValidation = {}
      allEntityTypes.forEach((entityType) => {
        result.entityTypeValidation![entityType as EntityTypeEnum] = {
          ...baseConfig.entityTypeValidation?.[entityType as EntityTypeEnum],
          ...overrideConfig.entityTypeValidation?.[entityType as EntityTypeEnum]
        }
      })
    }

    return result
  }

  const customValidation: CustomExportValidation = Object.keys(
    integrationValidationOverrides
  ).reduce((acc, integrationType) => {
    const integrationKey = integrationType as IntegrationTypeEnum
    acc[integrationKey] = createValidationForIntegration(integrationKey)
    return acc
  }, {} as CustomExportValidation)

  const customValidationForEntities = computed(() => {
    if (integrationMode.value) {
      const key = integrationMode.value.toLowerCase() as IntegrationTypeEnum

      // First try to get integration-specific validation
      let validation = customValidation[key]

      // If no specific validation exists, create one using base validation
      if (!validation) {
        validation = createValidationForIntegration(key)
      }

      if (validation) {
        const flatValidators = {}
        Object.keys(validation).forEach((groupKey) => {
          const group = validation[groupKey as EntityFieldsGroup]
          if (group && typeof group === 'object') {
            Object.assign(flatValidators, group)
          }
        })

        return {
          baseValidation: flatValidators,
          entityTypeValidation: validation.entityTypeValidation || {}
        }
      }
    }
    return null
  })

  const integrationMode = computed(() => {
    if (!caseData.value) return null
    const modes = [
      {
        name: 'Actaport',
        enabled: caseData.value.org.is_actaport_export_integration_enabled,
        id: null
      },
      {
        name: 'Advolux',
        enabled: caseData.value.org.is_advolux_export_integration_enabled,
        id: null
      },
      {
        name: 'Advoware',
        enabled: caseData.value.org.is_advoware_export_integration_enabled,
        id: null
      },
      {
        name: 'LawFirmSoftware',
        enabled: caseData.value.org.is_law_firm_software_exportation_enabled,
        id: null
      },
      {
        name: 'RaMicro',
        enabled: caseData.value.org.is_ra_micro_export_integration_enabled,
        id: null
      },
      {
        name: 'RaMicroApi',
        enabled: caseData.value.org.is_ra_micro_export_integration_enabled,
        id: null
      },
      {
        name: 'Jurnodes',
        enabled: caseData.value.org.is_jurnodes_export_integration_enabled,
        id: null
      },
      {
        name: 'DATEV',
        enabled: caseData.value.org.is_datev_export_integration_enabled,
        id: null
      },
      {
        name: 'Kleos',
        enabled: caseData.value.org.is_kleos_integration_enabled,
        id: null
      }
    ]

    const routeIntegration = router.currentRoute.value.params.integration_name
      ?.toString()
      .toLowerCase()
      .replace(/-/g, '')

    if (routeIntegration) {
      const matchedMode = modes.find(
        (mode) => mode.name.toLowerCase().replace(/-/g, '') === routeIntegration && mode.enabled
      )
      if (matchedMode) return matchedMode.name
    }

    const enabledMode = modes.find((mode) => mode.enabled)
    return enabledMode ? enabledMode.name : null
  })

  const caseRecordForLawFields = computed<CustomerCase | null>(
    () => caseStore.caseRecord as CustomerCase | null
  )

  const lawSpecificMappers = computed<GenericDataMapper[]>(() => {
    if (!caseRecordForLawFields.value?.data) return []
    let mappers = caseRecordForLawFields.value.data.mappers.filter(
      (mapper) =>
        mapper.is_law_specific_field && mapper.law_field !== undefined && mapper.law_field !== null
    ) as GenericDataMapper[]
    if (integrationMode.value === 'Jurnodes' && caseData.value?.org) {
      const allowed = jurnodesExportMapperKeys.value
      if (Array.isArray(allowed) && allowed.length > 0) {
        const allowedSet = new Set(allowed)
        const filtered = mappers.filter((mapper) => allowedSet.has(mapper.key))
        const byKey = new Map<string, GenericDataMapper>(
          filtered.map((m) => [m.key, m] as [string, GenericDataMapper])
        )
        mappers = allowed
          .map((key) => byKey.get(key))
          .filter((m): m is GenericDataMapper => m !== undefined)
      }
    }
    return mappers
  })

  const nonEmptyLawSpecificMappers = computed<GenericDataMapper[]>(() => {
    return lawSpecificMappers.value.filter((mapper) => {
      const [parent, field] = mapper.key.split('.') as [keyof LawData, string]
      const parentData = caseRecordForLawFields.value?.data?.[parent as 'law']
      if (!parentData || typeof parentData !== 'object') return false
      const value = (parentData as Record<string, unknown>)[field]
      if (value === undefined || value === null || value === '') return false
      if (Array.isArray(value) && value.length === 0) return false
      if (typeof value === 'object' && Object.keys(value as object).length === 0) return false
      return true
    })
  })

  const lawSpecificNonEmptyMapperKeys = computed<string[]>(() =>
    nonEmptyLawSpecificMappers.value.map((mapper) => mapper.key)
  )

  const showJurnodesLawSpecificDatamapper = computed(() => {
    if (integrationMode.value !== 'Jurnodes' || !caseData.value) return false
    const raw = caseData.value.org.jurnodes_export_law_specific_data as unknown
    if (typeof raw === 'boolean') return raw
    return raw === true
  })

  watch(
    () => nonEmptyLawSpecificMappers.value.map((mapper) => mapper.key),
    (keys) => {
      const prev = new Set(prevNonEmptyLawSpecificKeys.value)
      const retained = selectedLawSpecificMapperKeys.value.filter((key) =>
        keys.some((k) => k === key)
      )
      const next = [...retained]
      for (const key of keys) {
        if (!prev.has(key)) {
          next.push(key)
        }
      }
      prevNonEmptyLawSpecificKeys.value = [...keys]
      selectedLawSpecificMapperKeys.value = next
    },
    { immediate: true }
  )

  const downloadableContent = computed(() => {
    return ['Advolux', 'LawFirmSoftware', 'RaMicro', 'DATEV'].includes(
      integrationMode.value as string
    )
  })

  const integrationDisplayName = computed(() => {
    const displayNames: Record<string, string> = {
      Actaport: 'Actaport',
      Advolux: 'Advolux',
      Advoware: 'Advoware',
      LawFirmSoftware: 'Law Firm Software',
      RaMicro: 'RA-MICRO',
      RaMicroApi: 'RA-MICRO',
      Jurnodes: 'Jurnodes',
      DATEV: 'DATEV',
      Kleos: 'Kleos'
    }
    return displayNames[integrationMode.value || ''] || integrationMode.value || ''
  })

  const mappedSelectedEntities = computed(() => {
    if (!caseData.value?.data?.entities) {
      return ['client']
    }

    const entityTypes: string[] = ['client']

    const opponentEntities = caseData.value.data.entities.filter((entity) =>
      entity.relationships.some((rel) => (rel.relationship_type as string) === 'entities.opponent')
    )

    const isOpponentSelected = selectedEntitiesRelationships.value.some((uuid) =>
      opponentEntities.some((entity) => entity.uuid === uuid)
    )

    if (isOpponentSelected) {
      entityTypes.push('opponent')
    }

    return entityTypes
  })

  const isAllDataLoaded = computed(() => {
    return !isLoading.value && !isEntitiesLoading.value && !isFormsLoading.value
  })

  const hasPendingProposedChanges = computed(() => {
    return caseData.value?.data?.entities?.some(
      (entityRelationship) => entityRelationship.entity.proposed_changes.length > 0
    )
  })

  const canSubmitForm = computed(() => {
    if (integrationMode.value === 'RaMicroApi' && !documentSizeValid.value) {
      return false
    }
    if (totalNumberOfErrors.value > 0) {
      return false
    }
    if (!isAllDataLoaded.value) {
      return false
    }
    return true
  })

  const performActionText = computed(() => {
    if (downloadableContent.value) {
      return t('downloadCase')
    }
    return t('createCase')
  })

  const loadJurnodesExportMapperKeys = async () => {
    const orgUuid = caseData.value?.org?.uuid
    if (!orgUuid) {
      jurnodesExportMapperKeys.value = []
      return
    }
    try {
      const res = await OrganisationsService.organisationsJurnodesExportMapperKeysRetrieve({
        uuid: orgUuid
      })
      jurnodesExportMapperKeys.value = Array.isArray(res.mapper_keys) ? [...res.mapper_keys] : []
    } catch (e) {
      console.error('Failed to load Jurnodes export mapper keys:', e)
      jurnodesExportMapperKeys.value = []
    }
  }

  const fetchCaseData = async () => {
    try {
      const fetchedCaseData = (await CasesService.casesRetrieve({
        uuid: router.currentRoute.value.params.uuid.toString()
      })) as CustomerCase

      caseData.value = fetchedCaseData

      if (caseData.value) {
        apiPayload.value.mandateId = caseData.value.id
        apiPayload.value.integrationType = (integrationMode.value?.toLowerCase() ||
          '') as IntegrationTypeEnum
      }
    } catch (e) {
      error.value = t('failedToLoadCaseData')
      console.error('Error loading case data:', e)
      throw e
    } finally {
      isLoading.value = false
    }
  }

  const handleUpdatePayload = (updatedData: CustomerCase) => {
    if (updatedData) {
      apiPayload.value.integrationSpecificData = {
        ...apiPayload.value.integrationSpecificData,
        ...updatedData
      }
    }
  }

  const handleOnTotalNumberOfErrors = (total: number) => {
    totalNumberOfErrors.value = total
  }

  const handleExportOpponentData = (exportOpponentData: boolean) => {
    isOpponentExported.value = exportOpponentData
    ;(apiPayload.value.integrationSpecificData as any).export_opponent_information =
      exportOpponentData
  }

  const handleExportLegalInsuranceData = (val: boolean) => {
    exportLegalInsuranceData.value = val
    ;(apiPayload.value.integrationSpecificData as any).use_legal_insurance = val
  }

  const handleSelectEntitiesWithRelationships = (val: Array<string>) => {
    apiPayload.value.integrationSpecificData = {
      ...apiPayload.value.integrationSpecificData,
      entities_with_relationships: val
    }

    // Check if the selected entities have actually changed
    const previouslySelected = new Set(selectedEntitiesRelationships.value)
    const newlySelected = new Set(val)
    const hasChanged =
      previouslySelected.size !== newlySelected.size ||
      [...previouslySelected].some((uuid) => !newlySelected.has(uuid))

    selectedEntitiesRelationships.value = val

    // Only reset party matching state if selection actually changed
    if (hasChanged) {
      hasCheckedForMatches.value = false
      showPartyMatchingStep.value = false
      partyMatchingResult.value = null
      partyLinkSelections.value.clear()
    }
  }

  const handleFileDataUpdate = (newFileData: FileDataRequest[]) => {
    apiPayload.value.integrationSpecificData = {
      ...apiPayload.value.integrationSpecificData,
      fileData: newFileData
    }
  }

  // Actaport Party Matching methods
  const checkForPartyMatches = async () => {
    if (integrationMode.value !== 'Actaport') return
    if (!caseData.value?.uuid) return
    if (selectedEntitiesRelationships.value.length === 0) return

    isLoadingPartyMatches.value = true
    try {
      const response = await ActaportPartyMatchingService.actaportPartyMatchingFindMatchesCreate({
        requestBody: {
          case_uuid: caseData.value.uuid,
          participant_uuids: selectedEntitiesRelationships.value
        }
      })

      partyMatchingResult.value = response
      hasCheckedForMatches.value = true

      if (response.needs_user_review) {
        showPartyMatchingStep.value = true
      }
    } catch (error) {
      console.error('Failed to check for party matches:', error)
      // Continue without party matching if it fails
      hasCheckedForMatches.value = true
    } finally {
      isLoadingPartyMatches.value = false
    }
  }

  const handlePartyMatchingComplete = async (
    linkSelections: Map<string, LinkDecisionValueRequest>
  ) => {
    partyLinkSelections.value = linkSelections

    showPartyMatchingStep.value = false
    hasCheckedForMatches.value = true

    // Small delay to let the view update before submitting
    await new Promise((resolve) => setTimeout(resolve, 100))

    handleSubmit()
  }

  const handlePartyMatchingBack = () => {
    showPartyMatchingStep.value = false
    hasCheckedForMatches.value = false
  }

  const handleSaveInfo = async (infoType: 'client' | 'opponent', updatedData: any) => {
    if (caseData.value) {
      try {
        const processedData = { ...updatedData }

        if ('date_of_birth' in processedData) {
          processedData.date_of_birth =
            processedData.date_of_birth === '' ? null : processedData.date_of_birth
        }

        const response = await CasesService.casesPartialUpdate({
          uuid: caseData.value.uuid as string,
          requestBody: {
            data: {
              [infoType]: {
                ...caseData.value.data?.[infoType],
                ...processedData
              }
            }
          } as PatchedCustomerCaseRequest
        })

        if (caseData.value.data) {
          caseData.value.data[infoType] = {
            ...caseData.value.data[infoType],
            ...processedData
          }
        }
      } catch (e) {
        console.error(`Error updating ${infoType} information:`, e)
      }
    }
  }

  const handleLawSpecificMapperSave = debounce(
    async (key: string, mapperKey: string, value: unknown) => {
      await caseStore.saveDataWithMapperKey(mapperKey as AiAugmentedByMapperKeysEnum, value)
      const mapperLabel = caseRecordForLawFields.value?.data?.mappers.find(
        (m) => m.key === mapperKey
      )?.label
      toast({
        title: t('updatedFields'),
        description: mapperLabel ?? mapperKey,
        variant: 'default'
      })
    },
    1000
  )

  const applyJurnodesLawSpecificExportSelectionToPayload = () => {
    const data = apiPayload.value.integrationSpecificData as Record<string, unknown>
    if (integrationMode.value !== 'Jurnodes' || !showJurnodesLawSpecificDatamapper.value) {
      delete data.law_specific_mapper_keys
      return
    }
    data.law_specific_mapper_keys = [...selectedLawSpecificMapperKeys.value]
  }

  const validateAllForms = async () => {
    const formRefs = [
      { name: 'personalInformation', ref: personalInformationRef },
      { name: 'opponentInformation', ref: opponentInformationRef },
      { name: 'actaportForm', ref: actaportFormRef },
      { name: 'advoluxForm', ref: advoluxFormRef },
      { name: 'advowareForm', ref: advowareFormRef },
      { name: 'kleosForm', ref: kleosFormRef },
      { name: 'jurnodesForm', ref: jurnodesFormRef },
      { name: 'lawFirmSoftwareForm', ref: lawFirmSoftwareFormRef },
      { name: 'raMicroForm', ref: raMicroFormRef },
      { name: 'raMicroApiForm', ref: raMicroApiFormRef },
      { name: 'datevForm', ref: datevFormRef },
      { name: 'legalInsurance', ref: legalInsuranceRef }
    ]

    if (integrationMode.value === 'RaMicroApi' && !documentSizeValid.value) {
      validationErrors.value['documents'] = {
        size: t('documentSizeExceedsLimit')
      }
    }

    const validationResults = await Promise.all(
      formRefs.map(async ({ name, ref }) => {
        const formInstance = ref.value as any
        if (formInstance && typeof formInstance.validate === 'function') {
          if (!isOpponentExported.value && name === 'opponentInformation') {
            return { formName: name, valid: true, errors: {} }
          }
          const result = await formInstance.validate()
          return { formName: name, ...result }
        }
        return { formName: name, valid: true, errors: {} }
      })
    )

    const allFormsValid = validationResults.every((result) => result.valid)
    const errors = validationResults.reduce(
      (acc, result) => {
        if (!result.valid) {
          acc[result.formName] = result.errors
        }
        return acc
      },
      {} as Record<string, any>
    )

    validationErrors.value = errors

    return allFormsValid && totalNumberOfErrors.value === 0
  }

  const showValidationErrors = () => {
    const formRefs = [
      { name: 'personalInformation', ref: personalInformationRef },
      { name: 'opponentInformation', ref: opponentInformationRef },
      { name: 'actaportForm', ref: actaportFormRef },
      { name: 'advoluxForm', ref: advoluxFormRef },
      { name: 'advowareForm', ref: advowareFormRef },
      { name: 'kleosForm', ref: kleosFormRef },
      { name: 'jurnodesForm', ref: jurnodesFormRef },
      { name: 'lawFirmSoftwareForm', ref: lawFirmSoftwareFormRef },
      { name: 'raMicroForm', ref: raMicroFormRef },
      { name: 'datevForm', ref: datevFormRef },
      { name: 'legalInsurance', ref: legalInsuranceRef }
    ]

    formRefs.forEach(({ name, ref }) => {
      const formInstance = ref.value as any
      if (
        validationErrors.value[name] &&
        formInstance &&
        typeof formInstance.showErrors === 'function'
      ) {
        formInstance.showErrors(validationErrors.value[name])
      }
    })
  }

  const handleModalComplete = () => {
    showModal.value = false
    const path = `cases/${caseData.value?.id}/`
    const baseUrl = `${window.location.protocol}//${window.location.host}`
    const absoluteUrl = new URL(path, baseUrl).href
    window.location.href = absoluteUrl
  }

  const handleRetry = () => {
    modalStatus.value = 'loading'
    exportErrorDetails.value = ''
    exportWarnings.value = []
    handleSubmit()
  }

  const handleResetStatus = () => {
    modalStatus.value = 'loading'
    exportErrorDetails.value = ''
    canScheduleExport.value = false
    isSchedulingExport.value = false
    isRetryingExport.value = false
    hasManualRetryFailed.value = false
    showModal.value = false
  }

  /**
   * Handle immediate retry from the temporary error modal.
   * If retry fails again with a temporary error, don't show the same modal -
   * instead show a generic error to avoid blocking the user.
   */
  const handleTemporaryRetry = async () => {
    if (!caseData.value || !integrationMode.value) return

    isRetryingExport.value = true

    try {
      applyJurnodesLawSpecificExportSelectionToPayload()
      // Resubmit the export
      const response = (await CaseCreationIntegrationService.caseCreationIntegrationCreate({
        requestBody: apiPayload.value
      })) as any

      // Success - handle response based on type
      modalStatus.value = 'success'
      canScheduleExport.value = false
      hasManualRetryFailed.value = false

      // Handle file download if applicable
      if (downloadableContent.value && response.content) {
        const decodedContent = atob(response.content)
        const byteArray = new Uint8Array(decodedContent.length)
        for (let i = 0; i < decodedContent.length; i++) {
          byteArray[i] = decodedContent.charCodeAt(i)
        }
        const blob = new Blob([byteArray], { type: response.contentType })
        const blobUrl = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = response.fileName
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(blobUrl)
      }
    } catch (error: any) {
      // If this is another temporary error, don't show the modal again - go to regular error
      if (
        exportRetriesFlag.isEnabled.value &&
        error?.status === 503 &&
        error?.body?.errorType === 'temporary_service_unavailable'
      ) {
        // Show generic error instead of the same modal
        hasManualRetryFailed.value = true
        exportErrorDetails.value = t('temporaryErrorMessage')
        modalStatus.value = 'error'
      } else {
        // Different error - show regular error
        exportErrorDetails.value = error?.body?.message || error?.message || 'Unknown error'
        modalStatus.value = 'error'
      }
    } finally {
      isRetryingExport.value = false
    }
  }

  const handleScheduleExport = async () => {
    if (!caseData.value || !integrationMode.value) return

    isSchedulingExport.value = true

    try {
      applyJurnodesLawSpecificExportSelectionToPayload()
      await CaseCreationIntegrationService.caseCreationIntegrationScheduleExportCreate({
        requestBody: {
          integrationType:
            integrationMode.value.toLowerCase() as ScheduledExportRequestIntegrationTypeEnum,
          mandateId: caseData.value.id,
          integrationSpecificData: apiPayload.value.integrationSpecificData
        }
      })

      modalStatus.value = 'scheduled'
      canScheduleExport.value = false
    } catch (error: any) {
      exportErrorDetails.value = t('scheduleExportError')
      modalStatus.value = 'error'
    } finally {
      isSchedulingExport.value = false
    }
  }

  const formatAdvowareError = (errorMsg: string): string => {
    if (
      errorMsg.includes('authentication failed') ||
      errorMsg.includes('unauthorized') ||
      errorMsg.includes('401')
    ) {
      return t('advowareAuthError', { details: errorMsg })
    } else if (errorMsg.includes('Advoware API error')) {
      return t('advowareApiError', { details: errorMsg })
    }
    return errorMsg
  }

  const handlePendingChangesCancel = () => {
    showPendingChangesDialog.value = false
  }

  const handlePendingChangesReview = () => {
    showPendingChangesDialog.value = false
    entitiesRef.value?.selectFirstEntityWithProposedChanges()
  }

  const handleSubmit = async () => {
    if (integrationMode.value === 'RaMicroApi' && !documentSizeValid.value) {
      showErrorNotification.value = true
      errorMessage.value = t('documentSizeExceedsLimit')
      setTimeout(() => {
        showErrorNotification.value = false
      }, 5000)
      return
    }

    if (integrationMode.value === 'Actaport' && caseData.value) {
      ;(apiPayload.value.integrationSpecificData as ActaportDataRequest).wegen =
        caseData.value.data?.law?.topic || ''

      if (partyLinkSelections.value.size > 0) {
        const linkDecisions: Record<string, LinkDecisionValueRequest> = {}
        for (const [entityUuid, decision] of partyLinkSelections.value.entries()) {
          linkDecisions[entityUuid] = decision
        }
        ;(apiPayload.value.integrationSpecificData as ActaportDataRequest).link_decisions =
          linkDecisions
      }
    }

    applyJurnodesLawSpecificExportSelectionToPayload()

    const isValid = await validateAllForms()
    if (isValid) {
      if (integrationMode.value === 'Actaport' && !hasCheckedForMatches.value) {
        await checkForPartyMatches()
        if (showPartyMatchingStep.value) {
          return
        }
      }
      showModal.value = true
      modalStatus.value = 'loading'
      try {
        const response = (await CaseCreationIntegrationService.caseCreationIntegrationCreate({
          requestBody: apiPayload.value
        })) as any

        // Check if this is an Actaport export with case details
        if (integrationMode.value === 'Actaport' && response.actaport_case_number) {
          // Navigate to success page with case details as query params
          router.push({
            path: `/app/integrations/actaport/export/success/${caseData.value?.id}`,
            query: {
              actaport_case_number: response.actaport_case_number,
              file_name: response.file_name,
              file_number: response.file_number,
              last_updated: response.last_updated
            }
          })
          return
        } else {
          modalStatus.value = 'success'
          exportWarnings.value = response.warnings ?? []
          if (downloadableContent.value) {
            const decodedContent = atob(response.content)
            const byteArray = new Uint8Array(decodedContent.length)
            for (let i = 0; i < decodedContent.length; i++) {
              byteArray[i] = decodedContent.charCodeAt(i)
            }
            const blob = new Blob([byteArray], { type: response.contentType })
            const blobUrl = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = blobUrl
            link.download = response.fileName
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            URL.revokeObjectURL(blobUrl)
          }
        }
      } catch (error: any) {
        if (integrationMode.value === 'Actaport') {
          hasCheckedForMatches.value = false
          partyLinkSelections.value.clear()
        }

        // Check if this is a temporary error that can be scheduled
        if (
          exportRetriesFlag.isEnabled.value &&
          error?.status === 503 &&
          error?.body?.errorType === 'temporary_service_unavailable' &&
          error?.body?.canSchedule
        ) {
          modalStatus.value = 'temporary_error'
          canScheduleExport.value = true
          exportErrorDetails.value = '' // Message is handled by translations
          return
        }

        modalStatus.value = 'error'
        canScheduleExport.value = false

        if (error?.body?.message) {
          const errorMsg = error.body.message
          exportErrorDetails.value =
            integrationMode.value === 'Advoware' ? formatAdvowareError(errorMsg) : errorMsg
        } else if (error?.body && typeof error.body === 'object') {
          const fieldErrors: string[] = []
          for (const [field, messages] of Object.entries(error.body)) {
            if (Array.isArray(messages)) {
              messages.forEach((msg: string) => {
                fieldErrors.push(`${field}: ${msg}`)
              })
            }
          }
          exportErrorDetails.value =
            fieldErrors.length > 0
              ? `Validierungsfehler:\n${fieldErrors.join('\n')}`
              : error.message || 'An unexpected error occurred during export.'
        } else if (error?.message) {
          const errorMsg = error.message
          exportErrorDetails.value =
            integrationMode.value === 'Advoware' ? formatAdvowareError(errorMsg) : errorMsg
        } else {
          exportErrorDetails.value = 'An unexpected error occurred during export.'
        }
      }
    } else {
      showValidationErrors()
      showErrorNotification.value = true
      document.getElementById('entities-card')?.scrollIntoView({ behavior: 'smooth' })
      setTimeout(() => {
        showErrorNotification.value = false
      }, 5000)
    }
  }

  const getExistingExportId = (
    caseRecord: CustomerCase | null,
    integration: string | null
  ): string | null | undefined => {
    if (!caseRecord || !integration) return undefined
    const exportIdFields: Record<string, keyof CustomerCase> = {
      Actaport: 'actaport_case_id',
      Advoware: 'advoware_case_id',
      Kleos: 'kleos_reference'
    }
    const exportTimestampFields: Record<string, string> = {
      Actaport: 'sent_to_actaport_at',
      Advoware: 'sent_to_advoware_at',
      Kleos: 'sent_to_kleos_at',
      RaMicroApi: 'sent_to_ra_micro_at',
      Jurnodes: 'sent_to_jurnodes_at'
    }
    const idField = exportIdFields[integration]
    if (idField && caseRecord[idField]) return caseRecord[idField] as string

    const tsField = exportTimestampFields[integration]
    const record = caseRecord as Record<string, unknown>
    if (tsField && record[tsField]) return record[tsField] as string

    return undefined
  }

  const loadAllData = async () => {
    try {
      isLoading.value = true
      isEntitiesLoading.value = true
      isFormsLoading.value = true

      await fetchCaseData()

      if (getExistingExportId(caseData.value, integrationMode.value)) {
        window.location.href = `${window.location.protocol}//${window.location.host}/cases/${caseData.value?.id}/`
        return
      }

      if (integrationMode.value === 'Jurnodes') {
        await loadJurnodesExportMapperKeys()
      } else {
        jurnodesExportMapperKeys.value = []
      }

      await exportRetriesFlag.checkFlag()

      await Promise.all([
        FormsService.formsList().then(() => {
          isFormsLoading.value = false
        }),
        caseStore
          .loadCase(router.currentRoute.value.params.uuid as string, { waitForRelatedData: true })
          .then(() => {
            isEntitiesLoading.value = false
          })
      ])

      if (hasPendingProposedChanges.value) {
        showPendingChangesDialog.value = true
      }
    } catch (e) {
      error.value = t('failedToLoadData')
      console.error('Error loading data:', e)
    } finally {
      isFormsLoading.value = false
      isEntitiesLoading.value = false
    }
  }

  onMounted(async () => {
    await loadAllData()
  })

  watch(
    () => caseStore.caseRecord?.data?.entities,
    async () => {
      await fetchCaseData()
    },
    { deep: true }
  )
</script>

<template>
  <div class="flex min-h-screen flex-col">
    <div class="legacy-bg-color flex flex-grow items-start justify-center">
      <div
        v-if="showErrorNotification"
        class="fixed left-1/2 top-4 z-50 w-96 -translate-x-1/2 transform rounded border-l-4 border-red-500
          bg-red-100 p-4 text-red-700 shadow-md transition-all duration-500 ease-in-out"
        role="alert"
      >
        <div class="flex">
          <div class="py-1">
            <svg
              class="mr-4 h-6 w-6 fill-current text-red-500"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
            >
              <path
                d="M2.93 17.07A10 10 0 1 1 17.07 2.93 10 10 0 0 1 2.93 17.07zm12.73-1.41A8 8 0 1 0 4.34 4.34a8 8 0 0 0 11.32 11.32zM9 11V9h2v6H9v-4zm0-6h2v2H9V5z"
              />
            </svg>
          </div>
          <div>
            <p class="font-bold">{{ t('errorTitle') }}</p>
            <p class="text-sm">
              {{ errorMessage }}
            </p>
          </div>
        </div>
      </div>
      <div class="container px-16 py-6">
        <div class="flex w-full items-center justify-between pb-5">
          <h1
            v-if="integrationMode === 'RaMicroApi'"
            data-cy="export-header"
            class="flex-1 whitespace-nowrap text-2xl font-semibold"
          >
            {{ t('exportToRaMicro') }}
          </h1>
          <h1
            v-else
            data-cy="export-header"
            class="flex-1 whitespace-nowrap text-2xl font-semibold"
          >
            {{ t('exportToIntegration', { integration: integrationMode }) }}
          </h1>
          <a
            v-if="caseData"
            :href="`/cases/${caseData.id}/`"
            class="inline-flex items-center gap-x-2 rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm
              font-medium text-gray-800 shadow-sm hover:bg-gray-50 focus:bg-gray-50 focus:outline-none
              disabled:pointer-events-none disabled:opacity-50 dark:border-neutral-700 dark:bg-neutral-800
              dark:text-white dark:hover:bg-neutral-700 dark:focus:bg-neutral-700"
          >
            <ArrowLeftIcon class="h-4 w-4" />
            {{ t('backToCase') }}
          </a>
        </div>

        <div v-show="showPartyMatchingStep && integrationMode === 'Actaport' && caseData">
          <ActaportPartyMatchingStep
            v-if="showPartyMatchingStep && caseData"
            :case-uuid="caseData.uuid || ''"
            :selected-participant-uuids="selectedEntitiesRelationships"
            :matching-result="partyMatchingResult"
            @matching-complete="handlePartyMatchingComplete"
            @back="handlePartyMatchingBack"
          />
        </div>

        <div v-show="!showPartyMatchingStep || integrationMode !== 'Actaport' || !caseData">
          <div class="flex flex-col gap-6">
            <div
              v-if="isLoading || isEntitiesLoading || isFormsLoading"
              class="rounded-lg border border-gray-100 bg-white p-6 shadow-sm"
            >
              <div class="flex flex-col items-center justify-center py-4">
                <div class="loading-spinner">
                  <div
                    v-for="n in 4"
                    :key="n"
                    class="spinner-quad"
                  />
                </div>
                <p class="mt-6 text-base font-medium text-gray-900">{{ t('loadingDataText') }}</p>
              </div>
            </div>

            <div
              v-if="error"
              class="rounded-xl bg-red-100 p-4 text-center"
            >
              <p class="text-red-700">{{ error }}</p>
            </div>

            <template v-else-if="!isLoading && !isEntitiesLoading && !isFormsLoading">
              <template v-if="integrationMode === 'Actaport'">
                <div>
                  <ActaportForm
                    v-if="caseData"
                    ref="actaportFormRef"
                    :case-data="caseData"
                    :debounce-delay="1000"
                    :throttle-interval="1000"
                    :selected-entities-relationships="selectedEntitiesRelationships"
                    :mapped-entity-types="mappedSelectedEntities"
                    @update:payload="handleUpdatePayload"
                  />
                </div>
              </template>

              <template v-if="integrationMode === 'Advolux'">
                <div>
                  <AdvoluxForm
                    v-if="caseData"
                    ref="advoluxFormRef"
                    :case-data="caseData"
                    :debounce-delay="0"
                    :throttle-interval="0"
                    @update:payload="handleUpdatePayload"
                  />
                </div>
              </template>

              <template v-else-if="integrationMode === 'Advoware'">
                <div class="">
                  <AdvowareForm
                    v-if="caseStore.caseRecord"
                    ref="advowareFormRef"
                    :case-data="caseStore.caseRecord"
                    :debounce-delay="1000"
                    :throttle-interval="1000"
                    :is-opponent-exported="isOpponentExported"
                    :selected-entities-relationships="selectedEntitiesRelationships"
                    :mapped-entity-types="mappedSelectedEntities"
                    @update:payload="handleUpdatePayload"
                  />
                </div>
              </template>

              <template v-if="integrationMode === 'Kleos'">
                <div>
                  <KleosForm
                    v-if="caseData"
                    ref="kleosFormRef"
                    :case-data="caseData"
                    :debounce-delay="0"
                    :throttle-interval="0"
                    :is-opponent-exported="false"
                    :selected-entities-relationships="selectedEntitiesRelationships"
                    :mapped-entity-types="mappedSelectedEntities"
                    @update:payload="handleUpdatePayload"
                  />
                </div>
              </template>

              <template v-else-if="integrationMode === 'Jurnodes'">
                <div class="">
                  <JurnodesForm
                    v-if="caseData"
                    ref="jurnodesFormRef"
                    :case-data="caseData"
                    :debounce-delay="1000"
                    :throttle-interval="1000"
                    :isOpponentExported="isOpponentExported"
                    @update:payload="handleUpdatePayload"
                  />
                </div>
              </template>

              <template v-if="integrationMode === 'RaMicro' || integrationMode === 'RaMicroApi'">
                <div
                  class="rounded border-l-4 border-orange-500 bg-orange-100 p-4 text-orange-700 shadow-md transition-all
                    duration-500 ease-in-out"
                  role="alert"
                >
                  <div class="flex">
                    <div class="py-1">
                      <svg
                        class="mr-4 h-6 w-6 fill-current text-orange-500"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                      >
                        <path
                          d="M2.93 17.07A10 10 0 1 1 17.07 2.93 10 10 0 0 1 2.93 17.07zm12.73-1.41A8 8 0 1 0 4.34 4.34a8 8 0 0 0 11.32 11.32zM9 11V9h2v6H9v-4zm0-6h2v2H9V5z"
                        />
                      </svg>
                    </div>
                    <div>
                      <p class="font-bold">Achtung</p>
                      <p class="text-sm">
                        Diese Exportfunktion unterstützt den vollständigen Übertrag der Stammdaten,
                        sowohl des Mandanten als auch des Gegners.
                        <br />
                        <br />
                        Eine Ausnahme besteht bei der Übertragung aller Stammdaten als Erstanlage
                        für „sonstige Beteiligte“, die in den Fall involviert sind. In solchen
                        Fällen wird lediglich der Nachname der Person oder der Name der Institution
                        bzw. juristischen Person übernommen. Nach der Aktenanlage in RA-MICRO müssen
                        die fehlenden oder unvollständigen Angaben daher manuell ergänzt werden.
                        <br />
                        <br />
                        Sollten die Stammdaten dieser Beteiligten allerdings bereits als Adressat in
                        RA-MICRO hinterlegt sein, können diese direkt ausgewählt und überschrieben
                        werden.
                      </p>
                    </div>
                  </div>
                </div>
              </template>

              <template v-if="integrationMode === 'DATEV'">
                <div>
                  <DatevForm
                    v-if="caseData"
                    ref="datevFormRef"
                    :case-data="caseData"
                    :debounce-delay="1000"
                    :throttle-interval="1000"
                    @update:payload="handleUpdatePayload"
                  />
                </div>
              </template>

              <Entities
                id="entities-card"
                ref="entitiesRef"
                :unified-export-mode="true"
                :opponent-enabled="opponentEnabledList.includes(integrationMode as string)"
                :misc-entities-enabled="miscEntitiesEnabledList.includes(integrationMode as string)"
                :insurance-info-enabled="
                  integrationMode === 'Actaport' ||
                  integrationMode === 'RaMicro' ||
                  integrationMode === 'RaMicroApi' ||
                  integrationMode === 'DATEV'
                "
                :custom-export-validation="customValidationForEntities as any"
                @update:selected-entities-relationships="handleSelectEntitiesWithRelationships"
                @on-total-number-of-errors="handleOnTotalNumberOfErrors"
              />

              <div
                v-if="showJurnodesLawSpecificDatamapper && caseRecordForLawFields"
                class="mt-6 flex flex-col rounded-xl border border-gray-200 bg-white p-4 shadow-sm md:p-5"
              >
                <h2 class="mb-4 text-lg font-semibold text-gray-900">
                  {{ t('lawRelevantDataJurnodes') }}
                </h2>
                <div class="max-h-[min(70vh,35rem)] min-h-0 overflow-x-auto overflow-y-auto">
                  <DatamapperTable
                    v-model:export-selected-keys="selectedLawSpecificMapperKeys"
                    :show-keys="lawSpecificNonEmptyMapperKeys"
                    :object="caseRecordForLawFields"
                    :is-entity="false"
                    :mappers="caseRecordForLawFields.data?.mappers ?? []"
                    :mode="'edit'"
                    export-mode
                    data-cy="exportJurnodesLawSpecificTable"
                    @on-value-change="handleLawSpecificMapperSave"
                  />
                </div>
              </div>

              <AdvowareRolePreview
                v-if="integrationMode === 'Advoware' && caseData"
                :case-data="caseData"
                :selected-entities-relationships="selectedEntitiesRelationships"
              />

              <div v-if="false">
                <PersonalInformation
                  ref="personalInformationRef"
                  :case-data="caseData!"
                  :debounce-delay="5000"
                  :throttle-interval="5000"
                  @update:case-data="handleSaveInfo"
                />
              </div>

              <template v-if="false">
                <div>
                  <OpponentInformation
                    v-if="caseData"
                    ref="opponentInformationRef"
                    :case-data="caseData!"
                    :debounce-delay="5000"
                    :throttle-interval="5000"
                    @update:export-opponent-data="handleExportOpponentData"
                    @update:case-data="handleSaveInfo"
                  />
                </div>
              </template>

              <template v-if="integrationMode !== 'RaMicro'">
                <SelectDocumentSection
                  v-if="caseData"
                  :case-data="caseData"
                  :documents="caseStore.documents"
                  :integration-mode="integrationMode || ''"
                  :debounce-delay="1000"
                  :throttle-interval="1000"
                  @update:file-data="handleFileDataUpdate"
                  @size-validation="(isValid) => (documentSizeValid = isValid)"
                />
              </template>

              <div class="flex justify-end pt-2">
                <button
                  type="submit"
                  :class="[
                    `text-md rounded-lg px-6 py-2 font-semibold shadow-md transition duration-150 ease-in-out
                    focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2`,
                    canSubmitForm
                      ? 'bg-purple-600 text-white hover:bg-purple-700'
                      : 'cursor-not-allowed bg-gray-300 text-gray-500'
                  ]"
                  :disabled="!canSubmitForm"
                  @click="handleSubmit"
                >
                  {{ performActionText }}
                </button>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>

    <LoadingComponent
      :show="showModal"
      :status="modalStatus"
      :integration-name="integrationDisplayName"
      :error-message="exportErrorDetails"
      :warnings="exportWarnings"
      :can-schedule="canScheduleExport"
      :is-scheduling="isSchedulingExport"
      :is-retrying="isRetryingExport"
      :suppress-retry-on-warnings="integrationMode === 'Jurnodes'"
      @complete="handleModalComplete"
      @reset-status="handleResetStatus"
      @retry="handleRetry"
      @schedule="handleScheduleExport"
      @temporary-retry="handleTemporaryRetry"
    />

    <PendingProposedChangesDialog
      v-model:open="showPendingChangesDialog"
      source="export"
      :case-uuid="caseData?.uuid"
      @cancel="handlePendingChangesCancel"
      @review="handlePendingChangesReview"
    />
  </div>
</template>

<style scoped>
  .legacy-bg-color {
    background-color: #f8fafc !important;
  }

  .loading-spinner {
    position: relative;
    width: 80px;
    height: 80px;
    animation: spin 1.5s linear infinite;
  }

  .spinner-quad {
    position: absolute;
    width: 40%;
    height: 40%;
    border-radius: 50%;
    animation: pulse 1.5s ease-in-out infinite;
  }

  .spinner-quad:nth-child(1) {
    top: 0;
    left: 0;
    background-color: #9333ea;
  }

  .spinner-quad:nth-child(2) {
    top: 0;
    right: 0;
    background-color: #a855f7;
    animation-delay: 0.4s;
  }

  .spinner-quad:nth-child(3) {
    bottom: 0;
    right: 0;
    background-color: #c084fc;
    animation-delay: 0.8s;
  }

  .spinner-quad:nth-child(4) {
    bottom: 0;
    left: 0;
    background-color: #e9d5ff;
    animation-delay: 1.2s;
  }

  @keyframes spin {
    100% {
      transform: rotate(360deg);
    }
  }

  @keyframes pulse {
    0%,
    100% {
      transform: scale(0.5);
      opacity: 0.5;
    }
    50% {
      transform: scale(1);
      opacity: 1;
    }
  }
</style>
